/**
 * The Play screen's left column: who you are, and what you are working on.
 *
 * Play was one 3D backdrop, a Change Skin button and two corner stacks. Everything the player
 * had earned lived three nav clicks away, so the screen the game opens on said nothing about
 * the account looking at it — no level, no rank, no progress, no reason to press anything but
 * PLAY. That is the "nothing on the screen" problem, and it is a content problem rather than a
 * styling one: the data already existed, on endpoints two other routes were already reading.
 *
 * Two panels, both edge-anchored per `design/gdd/meta-visual-language.md` §2 rather than
 * dropped into the middle the spec deliberately keeps clear:
 *
 *   - **The dossier** (top left, directly under the nav row): a machined title bar, the
 *     callsign large, the level with its XP bar, the season tier on its own plate, and the
 *     three career figures worth a glance before pressing PLAY.
 *   - **The quest nav dot** (`syncQuestBadge`): the objectives panel itself came out in the
 *     1:1 pass against the lobby sheet — its left column is the dossier and nothing else — but
 *     the fetch survives headless, because it was the only publisher of the Quests nav dot.
 *
 * Both fetch on mount, both hide themselves entirely on failure, and both return a disposer —
 * the same contract `mountDailyReward` established, for the same reason: a menu whose corner
 * furniture can throw an error banner over the game's front door is worse than a menu without
 * the furniture.
 *
 * They are deliberately NOT on the mobile layout below `md`. At 375x812 the left edge is the
 * whole screen; a dossier there would cover the character it is describing, and the bottom nav
 * plus the daily button already own that column. Career and Quests are one tap away and are
 * the full versions of both panels.
 */
import { t } from '../../i18n';
import { api } from '../../../core/network/api';
import { icon, progressBar, statTile, type IconName } from '../ui';
import { bootIn } from '../fx';
import { daysRemaining, fetchSeason, type SeasonInfo } from './live-servers';
import { setNavBadge } from '../nav-badges-store';

/** Mirrors `career-route.ts`'s own constant — a backend env (default 100) no endpoint exposes. */
const XP_PER_LEVEL = 100;

interface Career {
  matches: number;
  wins: number;
  kills: number;
  deaths: number;
}

interface Ratios {
  killsPerDeath: number;
  winRate: number;
  accuracy: number;
}

interface Profile {
  username: string | null;
  level: number;
  xp: number;
  rankPoints: number;
  career: Career;
  ratios: Ratios;
}

interface QuestRow {
  progress: number;
  status: 'ACTIVE' | 'COMPLETED' | 'CLAIMED';
  quest: { name: string; questType: string; targetCount: number; rewardSoft: number; rewardXp: number };
}

export interface PlayPanel {
  element: HTMLElement;
  dispose(): void;
}

/**
 * The tier name used to be derived here, from hardcoded rank-point thresholds, on the stated
 * reasoning that "nothing exposes a tier *name* over the API".
 *
 * Something does: `GET /api/seasons/active` returns `userTier`, computed by `SeasonService`
 * from the same tier table that pays out the season's rewards. A second ladder in the client
 * is a ladder that can disagree with the one the player is actually climbing — and it would
 * disagree silently, since nothing renders both. It is gone; `mountPlayerDossier` reads the
 * server's answer and shows the raw points when there is no season configured.
 */

/**
 * A panel that starts hidden, reveals itself only once its data lands, and never errors.
 *
 * The reveal is an INLINE `display`, not `classList.remove('hidden')`. Removing the class is
 * what the first version did, and it also removed the `hidden md:block` breakpoint gate these
 * panels rely on to stay off the phone layout — so on a 375px screen the objectives panel
 * appeared anyway, over the daily-reward button, and swallowed the tap. `shell:check` walks the
 * nav and never presses that button, so nothing caught it but pressing it. An inline style and
 * a class list are separate channels: the class keeps owning the breakpoint, the style owns
 * "has this loaded".
 */
function hiddenPanel(extraClass: string): HTMLElement {
  const el = document.createElement('div');
  el.className = extraClass;
  el.style.display = 'none';
  return el;
}

export function mountPlayerDossier(): PlayPanel {
  // `fixed` and its own stacking context, matching the rest of Play's furniture. Below `md` it
  // stays `hidden` regardless of whether the fetch succeeded — see the header note.
  //
  // A painted armored window (`kit-window`) with a `kit-header` title plate, the callsign and
  // XP bar on its face, the tier on a steel plate and the career figures as `statTile`s —
  // 2026-10-12, "no flat surface anywhere". It was a quiet translucent sheet (2026-09-03, 1:1
  // with the lobby sheet); the owner asked for the painted kit everywhere.
  const element = hiddenPanel(
    'hidden md:block fixed z-[15] left-[var(--stage-inset)] top-[96px] w-[320px] lg:w-[392px]',
  );
  let disposed = false;

  void Promise.all([api.get<Profile>('/match/profile'), fetchSeason()])
    .then(([profile, season]: [Profile, SeasonInfo | null]) => {
      if (disposed) return;

      // The window is the element itself; its title plate is a `kit-header`.
      element.className += ' kit-window kit-pop [--kw-rim:24px] [--kw-pad:22px_22px_24px] flex flex-col gap-3';
      const header = document.createElement('div');
      header.className = 'kit-header [--kh:46px] -mx-1';
      const headerTitle = document.createElement('span');
      headerTitle.className = 'kit-title font-heading uppercase text-[17px] truncate';
      headerTitle.textContent = t('play.dossier.title');
      header.append(headerTitle);
      element.append(header);

      // The callsign, at the size the sheet draws it: the panel's headline.
      const callsign = document.createElement('div');
      callsign.className =
        'kit-title font-heading font-extrabold uppercase text-[32px] lg:text-[36px] leading-none truncate';
      callsign.textContent = profile.username ?? t('play.dossier.operator');
      element.append(callsign);

      // Level chevrons beside "LVL N", then the XP bar: the bar carries the progress, the line
      // under it the numbers — a bar with no denominator is a decoration.
      const levelLine = document.createElement('div');
      levelLine.className = 'flex items-center gap-2 kit-label font-heading uppercase tracking-[0.2em] text-[15px]';
      levelLine.append(icon('xp', { size: 28 }));
      const levelText = document.createElement('span');
      levelText.textContent = t('common.levelN', { level: profile.level });
      levelLine.append(levelText);
      element.append(levelLine);

      const xpInLevel = profile.xp % XP_PER_LEVEL;
      const bar = progressBar({
        value: xpInLevel,
        max: XP_PER_LEVEL,
        label: t('play.dossier.xpToNext'),
        accent: 'cyan',
      });
      element.append(bar);
      // The bar CHARGES on arrival — width runs 0 → value once the panel is on screen, so the
      // glowing tip travels and the reader watches their progress get measured, not asserted.
      // Double-rAF forces the 0% frame to paint before the flip; reduced motion (or no
      // matchMedia in jsdom) paints the final state immediately.
      const fill = bar.firstElementChild as HTMLElement | null;
      const reduced =
        typeof window.matchMedia === 'function' &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (fill && !reduced && xpInLevel > 0) {
        const finalWidth = fill.style.width;
        fill.style.width = '0%';
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            fill.style.width = finalWidth;
          });
        });
      }
      const xpLine = document.createElement('div');
      xpLine.className = 'flex items-center justify-between -mt-1';
      const xpNow = document.createElement('span');
      xpNow.className = 'text-[11px] text-text-dim tabular-nums';
      xpNow.textContent = t('play.xpProgress', { xp: xpInLevel, max: XP_PER_LEVEL });
      const xpNext = document.createElement('span');
      xpNext.className = 'font-heading uppercase tracking-[0.12em] text-[10px] text-text-dim';
      xpNext.textContent = t('play.toLevel', { level: profile.level + 1 });
      xpLine.append(xpNow, xpNext);
      element.append(xpLine);

      // ── The tier plate: a steel plate with the tier's MEDAL. ──────────────────────────────
      // The old objection — "the server sends a name with no ordinal, a metal would be a
      // guess" — died the day Season 1's tier table became ours (backend/prisma/seed.ts):
      // Bronze→Diamond are our own names, so hanging the kit's medal art on them is honest.
      // Unknown/renamed tier → the rank icon, never a wrong medal.
      const tier = document.createElement('div');
      tier.className = 'kit-plate [--kp-rim:14px] [--kp-pad:12px_16px] flex items-center gap-4';
      const TIER_ART: Record<string, string> = {
        bronze: '/game/ui/badges/bronze.png',
        silver: '/game/ui/badges/silver.png',
        gold: '/game/ui/badges/gold.png',
        platinum: '/game/ui/badges/platinum.png',
        diamond: '/game/ui/badges/diamond.png',
      };
      const artPath = TIER_ART[(season?.userTier ?? '').toLowerCase()];
      if (artPath) {
        const medal = document.createElement('img');
        medal.src = artPath;
        medal.alt = '';
        medal.setAttribute('aria-hidden', 'true');
        medal.draggable = false;
        medal.width = 56;
        medal.height = 56;
        medal.className = 'shrink-0 select-none [filter:drop-shadow(0_3px_5px_rgba(0,0,0,0.6))]';
        tier.append(medal);
      } else {
        tier.append(icon('rank', { size: 52 }));
      }
      const tierText = document.createElement('div');
      tierText.className = 'min-w-0 flex-1';
      const tierLabel = document.createElement('span');
      tierLabel.className = 'block font-heading uppercase tracking-[0.22em] text-[10px] text-text-dim';
      tierLabel.textContent = t('play.dossier.tier');
      const tierName = document.createElement('span');
      tierName.className = 'block kit-title font-heading uppercase text-[20px] truncate';
      // The server's tier, or nothing rather than a guess.
      tierName.textContent = season?.userTier ?? t('play.dossier.unranked');
      tierText.append(tierLabel, tierName);
      tier.append(tierText);
      element.append(tier);

      // The season deadline, when there is one: a ladder with no visible end date is a number
      // that goes up. A recessed readout in the window, the days left in a chip.
      if (season) {
        const days = daysRemaining(season);
        const seasonRow = document.createElement('div');
        seasonRow.className = 'kit-well flex items-center justify-between gap-2 px-3 py-1.5';
        const seasonName = document.createElement('span');
        seasonName.className = 'font-heading uppercase tracking-[0.08em] text-[10px] leading-tight text-text-dim min-w-0';
        const seasonLeft = document.createElement('span');
        seasonLeft.className = 'kit-chip [--kh:24px] shrink-0 font-heading text-[10px] tracking-[0.1em] uppercase ' +
          (days !== null && days <= 7 ? 'text-gold' : 'text-text-dim');
        seasonLeft.append(icon('time', { size: 18 }));
        const seasonLeftText = document.createElement('span');
        seasonLeftText.textContent =
          days === null
            ? t('play.season.ending')
            : days === 0
              ? t('play.season.lastDay')
              : t('play.season.daysLeft', { days });
        seasonLeft.append(seasonLeftText);
        // The rank points ride here, beside the season they are scored in.
        seasonName.textContent = t('play.season.line', {
          number: season.season.number,
          name: season.season.name,
          points: profile.rankPoints,
        });
        seasonRow.append(seasonName, seasonLeft);
        element.append(seasonRow);
      }

      // ── The readout block: three painted stat tiles, each with its own icon. ──────────────
      const stats = document.createElement('div');
      stats.className = 'grid grid-cols-3 gap-2';
      const figures: { label: string; value: string; art: IconName; accent: 'cyan' | 'default' }[] = [
        {
          label: t('play.dossier.kd'),
          value: profile.ratios.killsPerDeath.toFixed(2),
          art: 'kills',
          accent: profile.ratios.killsPerDeath >= 1 ? 'cyan' : 'default',
        },
        {
          label: t('play.dossier.winRate'),
          value: `${Math.round(profile.ratios.winRate * 100)}%`,
          art: 'trophy',
          accent: 'default',
        },
        {
          label: t('play.dossier.accuracy'),
          value: `${Math.round((profile.ratios.accuracy ?? 0) * 100)}%`,
          art: 'headshot',
          accent: 'default',
        },
      ];
      for (const figure of figures) {
        stats.append(statTile({ ...figure, size: 'md', centred: true }));
      }
      element.append(stats);

      element.style.display = '';
      // Powers on from the left the moment its data lands — the dossier is the screen's
      // "system online" beat, and it arrives like hardware, not like a div.
      bootIn(element, { fromX: -28, durationMs: 560 });
    })
    .catch(() => {
      // Left hidden on purpose. See the header note: the front door never shows an error.
    });

  return {
    element,
    dispose() {
      disposed = true;
      element.remove();
    },
  };
}

/**
 * The quest nav dot, without the panel that used to carry it.
 *
 * The lobby sheet's left column is the dossier and nothing else — the objectives panel came
 * out in the 1:1 pass. What must survive it is the side effect: this fetch was the only thing
 * publishing `quests` into `navBadgesStore`, and losing it would silently kill the red dot on
 * the Quests tab from the screen everyone starts on. Same never-throws contract as the panels.
 */
export function syncQuestBadge(): () => void {
  let disposed = false;
  void api
    .get<QuestRow[]>('/game/quests')
    .then((rows) => {
      if (disposed) return;
      // Every unclaimed finished quest counts, daily and weekly alike — the nav is answering
      // "is there anything on the Quests screen", and a finished weekly is exactly that.
      setNavBadge('quests', rows.some((row) => row.status === 'COMPLETED'));
    })
    .catch(() => {
      /* no dot rather than an error — same contract as the dossier */
    });
  return () => {
    disposed = true;
  };
}
